import {
  Heading,
  Avatar,
  Box,
  Image,
  Flex,
  Stack,
  Button,
  useColorModeValue,
  Text,
  Container,
  SimpleGrid,
  VStack,
  HStack,
  Icon,
  Divider,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useMediaQuery,
  Input,
  InputGroup,
  InputRightElement,
  useToast,
  Spinner,
  Card,
  CardBody,
  CardFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react';
import CatNavbar from './CatNavbar';
import MyadCards from './MyadCards.js';
import MyAnimals from './MyAnimals.js';
import React, { useEffect, useState } from 'react';
import {
  Card as MuiCard,
  CardMedia,
  Grid,
  CircularProgress,
  Avatar as MAvatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { SearchIcon, EmailIcon, PhoneIcon, EditIcon } from '@chakra-ui/icons';
import { FaUserFriends, FaShoppingBag, FaRegNewspaper, FaSeedling, FaFlask, FaPlus, FaPaw, FaClipboardList } from 'react-icons/fa';
import axios from 'axios';
import { FaStore } from "react-icons/fa";
import { API_BASE_URL } from './config/api_keys';

// PostCard Component to avoid useState in .map()
function PostCard({ post }) {
  const [showMore, setShowMore] = useState(false);
  const isLong = post.content?.length > 100;
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  return (
    <Box 
      borderWidth="1px" 
      borderRadius="lg" 
      overflow="hidden"
      bg={bgColor}
      borderColor={borderColor}
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-5px)', boxShadow: 'md' }}
      height="100%"
      display="flex"
      flexDirection="column"
    >
      {post.image && (
        <Image 
          src={post.image}
          alt="Post image"
          objectFit="cover"
          height="180px"
        />
      )}
      {post.video && (
        <Box as="video" controls height="180px" width="100%" objectFit="cover">
          <source src={post.video} />
        </Box>
      )}
      <Box p={4} flex="1">
        <Text
          fontSize="sm"
          color="gray.700"
          noOfLines={showMore ? undefined : 2}
        >
          {post.content}
        </Text>
        {isLong && (
          <Button
            size="xs"
            variant="link"
            colorScheme="green"
            onClick={() => setShowMore(!showMore)}
            mt={2}
          >
            {showMore ? 'Read less' : 'Read more'}
          </Button>
        )}
      </Box>
      <Box p={3} borderTopWidth="1px" borderColor={borderColor} bg="gray.50">
        <Text fontSize="xs" color="gray.500">
          {new Date(post.createdAt).toLocaleDateString()}
        </Text>
      </Box>
    </Box>
  );
}

// OrderStatusBadge Component
function OrderStatusBadge({ status }) {
  const statusConfig = {
    pending: { color: 'yellow', label: 'Pending' },
    confirmed: { color: 'blue', label: 'Confirmed' },
    shipped: { color: 'purple', label: 'Shipped' },
    delivered: { color: 'green', label: 'Delivered' },
    cancelled: { color: 'red', label: 'Cancelled' }
  };

  const config = statusConfig[status] || { color: 'gray', label: 'Unknown' };

  return (
    <Badge colorScheme={config.color}>
      {config.label}
    </Badge>
  );
}

// Main Profile Component
export default function Profile() {
  const authname = localStorage.getItem('authname');
  const picture = localStorage.getItem('authpicture');
  const userId = localStorage.getItem('userId');
  const authEmail = localStorage.getItem('authemail');
  const authPhone = localStorage.getItem('authphone') || "Not provided";

  const [userPosts, setUserPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(true);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [addFriendSearch, setAddFriendSearch] = useState('');
  const [addFriendResults, setAddFriendResults] = useState([]);
  const [addFriendLoading, setAddFriendLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const [userStores, setUserStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [userOrders, setUserOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [ordersError, setOrdersError] = useState('');

  const bgColor = useColorModeValue('white', 'gray.800');
  const bgGradient = useColorModeValue('linear(to-r, green.100, teal.50)', 'linear(to-r, green.900, teal.900)');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const toast = useToast();

  // Set tab index from URL parameter if present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam === 'animals') {
      setTabIndex(2); // Animals tab is the 3rd tab (index 2)
    }
  }, []);

  useEffect(() => {
    const fetchUserPosts = async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://api.agromanch.com/api/posts/user/${userId}`);
        const data = await res.json();
        setUserPosts(data);
      } catch (err) {
        setUserPosts([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchFriends = async () => {
      setLoadingFriends(true);
      try {
        const res = await axios.get(`https://api.agromanch.com/api/friends/list/${userId}`);
        setFriends(res.data);
      } catch {
        setFriends([]);
      } finally {
        setLoadingFriends(false);
      }
    };

    const fetchRequests = async () => {
      setLoadingRequests(true);
      try {
        const res = await axios.get(`https://api.agromanch.com/api/friends/requests/${userId}`);
        setRequests(res.data);
      } catch {
        setRequests([]);
      } finally {
        setLoadingRequests(false);
      }
    };
    
    const fetchUserStores = async () => {
      setLoadingStores(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          setUserStores([]);
          return;
        }
        
        const response = await axios.get(`${API_BASE_URL}/api/my-stores`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setUserStores(response.data);
      } catch (error) {
        console.error("Error fetching user stores:", error);
        setUserStores([]);
      } finally {
        setLoadingStores(false);
      }
    };

    const fetchUserOrders = async () => {
      setLoadingOrders(true);
      setOrdersError('');
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          setOrdersError('You need to be logged in to view your orders');
          setLoadingOrders(false);
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/api/admin/product-orders/my-orders`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          setUserOrders(response.data.orders);
        } else {
          setOrdersError(response.data.error || 'Failed to fetch orders');
        }
      } catch (error) {
        console.error("Error fetching user orders:", error);
        if (error.response && error.response.status === 401) {
          setOrdersError('You need to be logged in to view your orders');
        } else {
          setOrdersError('Failed to load orders. Please try again later.');
        }
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchUserPosts();
    fetchFriends();
    fetchRequests();
    fetchUserStores();
    fetchUserOrders();
  }, [userId]);

  const handleAccept = async (fromUserId) => {
    try {
      await axios.post('https://api.agromanch.com/api/friends/accept', { userId, fromUserId });
      const res1 = await axios.get(`https://api.agromanch.com/api/friends/list/${userId}`);
      setFriends(res1.data);
      const res2 = await axios.get(`https://api.agromanch.com/api/friends/requests/${userId}`);
      setRequests(res2.data);
    } catch {}
  };

  const handleAddFriendSearch = async (e) => {
    e.preventDefault();
    setAddFriendLoading(true);
    setAddFriendResults([]);
    try {
      const res = await axios.get(`https://api.agromanch.com/api/search-users?q=${encodeURIComponent(addFriendSearch)}`);
      setAddFriendResults(res.data);
    } catch {
      setAddFriendResults([]);
    } finally {
      setAddFriendLoading(false);
    }
  };

  const handleSendFriendRequest = async (toUserId) => {
    try {
      await axios.post('https://api.agromanch.com/api/friends/request', {
        fromUserId: userId,
        toUserId,
      });
    } catch {}
  };

  return (
    <Box bg={useColorModeValue('gray.50', 'gray.900')} minH="100vh">
      <Container maxW="container.xl" py={6}>
        <Flex direction={{ base: 'column', md: 'row' }} gap={6}>
          {/* Profile Card */}
          <Box 
            w={{ base: 'full', md: '300px' }}
            bg={bgColor}
            boxShadow="md"
            borderRadius="lg"
            overflow="hidden"
          >
            <Box 
              bgGradient={bgGradient}
              h="120px"
              position="relative"
            />
            <Flex justify="center" mt="-50px">
              <Avatar
                size="xl"
                src={picture}
                alt={authname}
                border="4px solid white"
                boxShadow="lg"
              />
            </Flex>
            <VStack p={6} spacing={3} align="center">
              <Heading size="md">{authname}</Heading>
              <Badge colorScheme="green" px={2} py={1} borderRadius="full">Farmer</Badge>
              
              <Divider />
              
              <HStack w="full" justify="center" spacing={4}>
                <VStack>
                  <Text fontWeight="bold">{friends.length}</Text>
                  <Text fontSize="xs" color="gray.500">Friends</Text>
                </VStack>
                <VStack>
                  <Text fontWeight="bold">{userPosts.length}</Text>
                  <Text fontSize="xs" color="gray.500">Posts</Text>
                </VStack>
                <VStack>
                  <Text fontWeight="bold">12</Text>
                  <Text fontSize="xs" color="gray.500">Products</Text>
                </VStack>
              </HStack>
              
              <Divider />
              
              <VStack align="start" w="full" spacing={2}>
                <HStack>
                  <Icon as={EmailIcon} color="green.500" />
                  <Text fontSize="sm" color={textColor} noOfLines={1}>{authEmail}</Text>
                </HStack>
                <HStack>
                  <Icon as={PhoneIcon} color="green.500" />
                  <Text fontSize="sm" color={textColor}>{authPhone}</Text>
                </HStack>
              </VStack>
              
              <VStack spacing={2} w="full">
                <Button
                  as="a"
                  href="/editprofile"
                  leftIcon={<EditIcon />}
                  colorScheme="green"
                  variant="outline"
                  size="sm"
                  w="full"
                >
                  Edit Profile
                </Button>
                <Button
                  as="a"
                  href="/create-store"
                  leftIcon={<FaStore />}
                  colorScheme="teal"
                  variant="outline"
                  size="sm"
                  w="full"
                >
                  Create Store
                </Button>
              </VStack>
            </VStack>
          </Box>

          {/* Content Area */}
          <Box flex="1">
            <Tabs 
              colorScheme="green" 
              variant="soft-rounded" 
              onChange={(index) => setTabIndex(index)}
              index={tabIndex}
              isLazy
            >
              <TabList 
                overflowX="auto" 
                overflowY="hidden" 
                css={{
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
                mb={4}
              >
                <Tab><Icon as={FaShoppingBag} mr={2} /> Products</Tab>
                <Tab><Icon as={FaStore} mr={2} /> My Store</Tab>
                <Tab><Icon as={FaPaw} mr={2} /> Animals</Tab>
                <Tab><Icon as={FaRegNewspaper} mr={2} /> Posts</Tab>
                <Tab><Icon as={FaClipboardList} mr={2} /> Orders</Tab>
                <Tab><Icon as={FaUserFriends} mr={2} /> Friends</Tab>
                <Tab>Add Friend</Tab>
                {requests.length > 0 && (
                  <Tab>
                    Requests
                    <Badge ml={2} colorScheme="red" borderRadius="full">{requests.length}</Badge>
                  </Tab>
                )}
              </TabList>

              <TabPanels>
                {/* Products Tab */}
                <TabPanel p={0}>
                  <Box 
                    bg={bgColor} 
                    borderRadius="lg" 
                    p={4} 
                    boxShadow="sm"
                  >
                    <MyadCards />
                  </Box>
                </TabPanel>
                
                {/* My Store Tab */}
                <TabPanel p={0}>
                  <Box 
                    bg={bgColor} 
                    borderRadius="lg" 
                    p={4} 
                    boxShadow="sm"
                  >
                    <Heading size="md" mb={4}>My Stores</Heading>
                    {loadingStores ? (
                      <Flex justify="center" py={10}>
                        <Spinner size="xl" color="green.500" />
                      </Flex>
                    ) : userStores.length === 0 ? (
                      <Box textAlign="center" py={10}>
                        <Text color="gray.500" mb={4}>You don't have any stores yet.</Text>
                        <Button
                          as="a"
                          href="/create-store"
                          leftIcon={<FaStore />}
                          colorScheme="green"
                          size="md"
                        >
                          Create a Store
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
                          {userStores.map((store) => (
                            <Card key={store._id} overflow="hidden" variant="outline" borderColor={borderColor}>
                              <Box position="relative">
                                <Image 
                                  src={store.coverImage || "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1932&q=80"} 
                                  alt={store.name}
                                  height="120px"
                                  width="100%"
                                  objectFit="cover"
                                />
                                {store.isVerified && (
                                  <Badge 
                                    position="absolute" 
                                    top="2" 
                                    right="2" 
                                    colorScheme="green"
                                    fontSize="0.8em"
                                    px={2}
                                    py={1}
                                    borderRadius="full"
                                  >
                                    Verified
                                  </Badge>
                                )}
                              </Box>
                              <CardBody>
                                <Flex align="center" mb={2}>
                                  <Avatar 
                                    src={store.logo} 
                                    size="sm" 
                                    mr={2} 
                                    bg="green.500"
                                    icon={<FaStore color="white" />}
                                  />
                                  <Heading size="md">{store.name}</Heading>
                                </Flex>
                                <Text noOfLines={2} color="gray.600" fontSize="sm" mb={3}>
                                  {store.description}
                                </Text>
                                <HStack spacing={2} mb={2}>
                                  <Badge colorScheme="blue">{store.address.city}</Badge>
                                  <Badge colorScheme="purple">{store.address.state}</Badge>
                                  <Badge colorScheme={store.isVerified ? "green" : "yellow"}>
                                    {store.verificationStatus}
                                  </Badge>
                                </HStack>
                              </CardBody>
                              <CardFooter pt={0}>
                                <Button 
                                  as="a" 
                                  href={`/store/${store._id}`}
                                  colorScheme="green" 
                                  size="sm" 
                                  width="full"
                                >
                                  Manage Store
                                </Button>
                              </CardFooter>
                            </Card>
                          ))}
                        </SimpleGrid>
                        
                        {userStores.some(store => store.isVerified) && (
                          <Box mt={6}>
                            <Heading size="md" mb={4}>Add Specialized Products</Heading>
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                              <Card 
                                direction="row" 
                                overflow="hidden" 
                                variant="outline" 
                                borderColor={borderColor}
                                _hover={{ transform: 'translateY(-5px)', transition: '0.3s' }}
                                as="a"
                                href="/components/Store/AddSpecializedProduct?type=fertilizer"
                              >
                                <Flex align="center" p={4}>
                                  <Icon as={FaSeedling} boxSize={8} color="green.500" mr={4} />
                                  <Box>
                                    <Heading size="sm">Add Fertilizer</Heading>
                                    <Text fontSize="xs" color="gray.500">List fertilizer products in your store</Text>
                                  </Box>
                                </Flex>
                              </Card>
                              
                              <Card 
                                direction="row" 
                                overflow="hidden" 
                                variant="outline" 
                                borderColor={borderColor}
                                _hover={{ transform: 'translateY(-5px)', transition: '0.3s' }}
                                as="a"
                                href="/components/Store/AddSpecializedProduct?type=medicine"
                              >
                                <Flex align="center" p={4}>
                                  <Icon as={FaFlask} boxSize={8} color="blue.500" mr={4} />
                                  <Box>
                                    <Heading size="sm">Add Medicine</Heading>
                                    <Text fontSize="xs" color="gray.500">List agricultural medicines in your store</Text>
                                  </Box>
                                </Flex>
                              </Card>
                              
                              <Card 
                                direction="row" 
                                overflow="hidden" 
                                variant="outline" 
                                borderColor={borderColor}
                                _hover={{ transform: 'translateY(-5px)', transition: '0.3s' }}
                                as="a"
                                href="/components/Store/AddSpecializedProduct"
                              >
                                <Flex align="center" p={4}>
                                  <Icon as={FaPlus} boxSize={8} color="purple.500" mr={4} />
                                  <Box>
                                    <Heading size="sm">Add Other Products</Heading>
                                    <Text fontSize="xs" color="gray.500">List other agricultural products</Text>
                                  </Box>
                                </Flex>
                              </Card>
                            </SimpleGrid>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </TabPanel>

                {/* Animals Tab */}
                <TabPanel p={0}>
                  <MyAnimals />
                </TabPanel>

                {/* Posts Tab */}
                <TabPanel p={0}>
                  <Box 
                    bg={bgColor} 
                    borderRadius="lg" 
                    p={4} 
                    boxShadow="sm"
                  >
                    <Heading size="md" mb={4}>My Posts</Heading>
                    {loading ? (
                      <Flex justify="center" py={10}>
                        <CircularProgress size={30} />
                      </Flex>
                    ) : userPosts.length === 0 ? (
                      <Box textAlign="center" py={10}>
                        <Text color="gray.500">No posts yet.</Text>
                      </Box>
                    ) : (
                      <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                        {userPosts.map((post) => (
                          <PostCard post={post} key={post._id} />
                        ))}
                      </SimpleGrid>
                    )}
                  </Box>
                </TabPanel>

                {/* Orders Tab */}
                <TabPanel p={0}>
                  <Box 
                    bg={bgColor} 
                    borderRadius="lg" 
                    p={4} 
                    boxShadow="sm"
                  >
                    <Heading size="md" mb={4}>My Orders</Heading>
                    {loadingOrders ? (
                      <Flex justify="center" py={10}>
                        <Spinner size="xl" color="green.500" />
                      </Flex>
                    ) : ordersError ? (
                      <Alert status="error" borderRadius="md">
                        <AlertIcon />
                        <Box>
                          <AlertTitle>Error loading orders</AlertTitle>
                          <AlertDescription>{ordersError}</AlertDescription>
                        </Box>
                      </Alert>
                    ) : userOrders.length === 0 ? (
                      <Box textAlign="center" py={10}>
                        <Text color="gray.500">You haven't placed any orders yet.</Text>
                        <Button
                          as="a"
                          href="/admin-products"
                          colorScheme="green"
                          size="md"
                          mt={4}
                        >
                          Browse Products
                        </Button>
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table variant="simple">
                          <Thead>
                            <Tr>
                              <Th>Product</Th>
                              <Th>Status</Th>
                              <Th>Date</Th>
                              <Th>Amount</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {userOrders.map((order) => (
                              <Tr key={order._id}>
                                <Td>
                                  <Flex align="center">
                                    {order.productSnapshot?.images && order.productSnapshot.images.length > 0 ? (
                                      <Image 
                                        src={order.productSnapshot.images[0]} 
                                        alt={order.productSnapshot.name}
                                        boxSize="50px"
                                        objectFit="cover"
                                        borderRadius="md"
                                        mr={3}
                                      />
                                    ) : (
                                      <Box 
                                        boxSize="50px" 
                                        bg="gray.200" 
                                        borderRadius="md" 
                                        mr={3}
                                      />
                                    )}
                                    <Box>
                                      <Text fontWeight="medium">{order.productSnapshot?.name || 'Unknown Product'}</Text>
                                      <Text fontSize="sm" color="gray.500">{order.productSnapshot?.type || ''}</Text>
                                    </Box>
                                  </Flex>
                                </Td>
                                <Td>
                                  <OrderStatusBadge status={order.status} />
                                </Td>
                                <Td>
                                  <Text fontSize="sm">{new Date(order.createdAt).toLocaleDateString()}</Text>
                                </Td>
                                <Td>
                                  <Text fontWeight="bold">₹{order.productSnapshot?.sellPrice || 0}</Text>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </TabPanel>

                {/* Friends Tab */}
                <TabPanel p={0}>
                  <Box 
                    bg={bgColor} 
                    borderRadius="lg" 
                    p={4} 
                    boxShadow="sm"
                  >
                    <Heading size="md" mb={4}>Friends ({friends.length})</Heading>
                    {loadingFriends ? (
                      <Flex justify="center" py={10}>
                        <CircularProgress size={30} />
                      </Flex>
                    ) : friends.length === 0 ? (
                      <Box textAlign="center" py={10}>
                        <Text color="gray.500">No friends yet.</Text>
                      </Box>
                    ) : (
                      <SimpleGrid columns={{ base: 1, sm: 2, md: 2 }} spacing={4}>
                        {friends.map((friend) => (
                          <Box 
                            key={friend._id} 
                            p={3} 
                            borderWidth="1px" 
                            borderRadius="md" 
                            display="flex" 
                            alignItems="center"
                          >
                            <Avatar src={friend.picture} size="md" mr={3} />
                            <Box>
                              <Text fontWeight="medium">{friend.name}</Text>
                              <Text fontSize="sm" color="gray.500">{friend.email}</Text>
                            </Box>
                          </Box>
                        ))}
                      </SimpleGrid>
                    )}
                  </Box>
                </TabPanel>

                {/* Add Friend Tab */}
                <TabPanel p={0}>
                  <Box 
                    bg={bgColor} 
                    borderRadius="lg" 
                    p={4} 
                    boxShadow="sm"
                  >
                    <Heading size="md" mb={4}>Find Friends</Heading>
                    <form onSubmit={handleAddFriendSearch}>
                      <InputGroup mb={4}>
                        <Input 
                          placeholder="Search by name or email..." 
                          value={addFriendSearch}
                          onChange={(e) => setAddFriendSearch(e.target.value)}
                        />
                        <InputRightElement>
                          <Button type="submit" size="sm" colorScheme="green" isLoading={addFriendLoading}>
                            <SearchIcon />
                          </Button>
                        </InputRightElement>
                      </InputGroup>
                    </form>
                    
                    {addFriendLoading ? (
                      <Flex justify="center" py={10}>
                        <CircularProgress size={30} />
                      </Flex>
                    ) : addFriendResults.length > 0 ? (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {addFriendResults.map((user) => (
                          <Box 
                            key={user._id} 
                            p={3} 
                            borderWidth="1px" 
                            borderRadius="md" 
                            display="flex" 
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Flex align="center">
                              <Avatar src={user.picture} size="md" mr={3} />
                              <Box>
                                <Text fontWeight="medium">{user.name}</Text>
                                <Text fontSize="sm" color="gray.500">{user.email}</Text>
                              </Box>
                            </Flex>
                            <Button 
                              size="sm" 
                              colorScheme="green" 
                              onClick={() => handleSendFriendRequest(user._id)}
                            >
                              Add
                            </Button>
                          </Box>
                        ))}
                      </SimpleGrid>
                    ) : addFriendSearch ? (
                      <Box textAlign="center" py={10}>
                        <Text color="gray.500">No users found.</Text>
                      </Box>
                    ) : null}
                  </Box>
                </TabPanel>

                {/* Requests Tab */}
                <TabPanel p={0}>
                  <Box 
                    bg={bgColor} 
                    borderRadius="lg" 
                    p={4} 
                    boxShadow="sm"
                  >
                    <Heading size="md" mb={4}>Friend Requests</Heading>
                    {loadingRequests ? (
                      <Flex justify="center" py={10}>
                        <CircularProgress size={30} />
                      </Flex>
                    ) : requests.length === 0 ? (
                      <Box textAlign="center" py={10}>
                        <Text color="gray.500">No pending requests.</Text>
                      </Box>
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {requests.map((request) => (
                          <Box 
                            key={request._id} 
                            p={3} 
                            borderWidth="1px" 
                            borderRadius="md" 
                            display="flex" 
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Flex align="center">
                              <Avatar src={request.picture} size="md" mr={3} />
                              <Box>
                                <Text fontWeight="medium">{request.name}</Text>
                                <Text fontSize="sm" color="gray.500">{request.email}</Text>
                              </Box>
                            </Flex>
                            <Button 
                              size="sm" 
                              colorScheme="green" 
                              onClick={() => handleAccept(request._id)}
                            >
                              Accept
                            </Button>
                          </Box>
                        ))}
                      </SimpleGrid>
                    )}
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
}